import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  temErro: boolean;
}

/**
 * Rede de segurança: sem isso, qualquer erro não tratado durante o render
 * (ex: acessar uma propriedade de um objeto undefined depois de uma
 * atualização de dados) derruba a árvore de componentes inteira e deixa a
 * tela em branco até o usuário recarregar a página manualmente. Com isso,
 * o erro fica contido e a pessoa consegue voltar a usar o sistema sem
 * perder o que estava fazendo em outras abas/partes do app.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { temErro: false };
  }

  static getDerivedStateFromError(): State {
    return { temErro: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Erro não tratado capturado pelo ErrorBoundary:', error, info);
  }

  handleRecarregar = () => {
    window.location.reload();
  };

  handleTentarNovamente = () => {
    this.setState({ temErro: false });
  };

  render() {
    if (this.state.temErro) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Ops, algo deu errado</h2>
            <p className="text-gray-600 mb-6">
              Ocorreu um erro inesperado nesta tela. Você pode tentar novamente ou recarregar a página.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleTentarNovamente}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Tentar novamente
              </button>
              <button
                onClick={this.handleRecarregar}
                className="px-4 py-2 bg-bravo-primary text-white rounded-md hover:bg-red-700"
              >
                Recarregar página
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
